import * as feedActions from '../../actions/feed.actions';
import * as widgetActions from '../../actions/widget.actions';

import { Actions, ofType } from '@ngrx/effects';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Feed, Post, User, Widget } from 'src/app/core/models/core.models';
import {
  FeedPostViewMode,
  WidgetViewProperties,
} from '../../services/feed.service';
import {
  NomDateTimeFormats,
  NomObjectClass,
  NomObjectTypes,
  WidgetType,
} from '../../constants';
import {
  SidebarButtonBehavior,
  SidebarButtonConfig,
} from '../../components/sidebar/sidebar.component';
import { Store, select } from '@ngrx/store';
import {
  clearSelectedObjects,
  selectObjects,
} from '../../actions/selected.actons';
import {
  loadExportSchedules,
  toggleWidgetEditMode,
} from '../../actions/object.actions';
import { objectChanged, periodChanged } from '../../actions/core.actions';
import {
  openPostDetails,
  togglePostParentThread,
} from '../../actions/post.actions';
import {
  selectAppliedAlgorithmPostIds,
  selectAttachedFeedAlgorithms,
  selectCountSelectedWidgets,
  selectCurrentObject,
  selectCurrentObjectEditMode,
  selectExportSchedulesCount,
  selectFeed,
  selectFeedAlertsCount,
  selectFeedCurrentPage,
  selectFeedItemsPerPage,
  selectFeedPosts,
  selectFeedPostsLoading,
  selectFeedSelectedFilterItems,
  selectFeedTimeShown,
  selectFeedViewConfig,
  selectIsLoadingWidgets,
  selectLoadingAlgorithmPostIds,
  selectObjectIsExporting,
  selectObjectWidgetViewProperties,
  selectPostByVideoUrlTargetParam,
  selectRemovedItemsByFeed,
  selectSelectedPlaceholderId,
  selectSelectedPostId,
  selectUser,
  selectWidgetEditMode,
  selectWidgetById,
  selectWidgetsByFilterString,
} from '../../reducers';

import { ActivatedRoute } from '@angular/router';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { FormGroup } from '@angular/forms';
import { FormsService } from '../../services/forms.service';
import { GridStackNode, GridStackOptions } from 'gridstack';
import { GridstackComponent } from 'gridstack/dist/angular';
import { GridsterConfigService } from '../../services/gridster-config.service';
import { LoggerService } from '@services/logger.service';
import { ObjectActionsService } from '../../services/object-actions.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { PanelComponentMappingService } from '../../services/panel-component-mapping.service';
import { PostCardComponent } from '../../components/cards/post-card/post-card.component';
import { PostListComponent } from '../../components/post-list/post-list.component';
import { PropertyViewConfig } from '../../services/feed-settings.service';
import { SearchBuilderData } from '../../components/search-builder/search-builder.component';
import { ShareMode } from '../../components/popups/share-dialog/share-dialog';
import { SidebarService } from '../../services/sidebar.service';
import { SortableResult } from '../../services/sortable-engine.service';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UserFacadeService } from '@services/user-facade.service';
import { UtilsService } from '../../services/utils.service';
import { WidgetService } from '../../services/widget.service';
import { clearSelectedPrincipals } from '../../actions/principals.actions';
import cloneDeep from 'lodash-es/cloneDeep';
import { createWidget } from '../../actions/widget.actions';
import { loadWidgetsSuccess } from '../../actions/widget.actions';
import { selectFeedTotalItemsCount } from '../../reducers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-feed',
  templateUrl: './feed.component.html',
  styleUrls: ['./feed.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
// Represents the FeedComponent class.
// This component is responsible for displaying and managing the feed, including posts and widgets.
// It handles user interactions and communicates with the store to retrieve and update data.
export class FeedComponent
  implements OnDestroy, AfterViewInit, AfterViewChecked
{
  @ViewChild('sidebar') sidebar: ElementRef;

  private _gridstack: GridstackComponent;
  @ViewChild(GridstackComponent)
  set gridstack(value: GridstackComponent) {
    this._gridstack = value;
    if (value) {
      this.setupGridstackEventListeners();
    }
  }
  get gridstack(): GridstackComponent {
    return this._gridstack;
  }

  @ViewChild('postList') postList: PostListComponent;

  // Grid configuration from service
  gridstackOptions: GridStackOptions;

  // * track which Widget is currently being resized
  resizingWidgetId$ = new BehaviorSubject<string | null>(null);
  // * track if any widget is currently being dragged
  isDragging$ = new BehaviorSubject<boolean>(false);

  feed$: Observable<Feed>;
  objectViewProperties$: Observable<WidgetViewProperties>;
  posts$: Observable<Array<Post>>;
  widgets$: Observable<Array<Widget>>;
  widgetsWithGridOptions$: Observable<
    Array<{ widget: Widget; gridOptions: GridStackNode }>
  >;
  selectedPostId$: Observable<string>;
  postsLoading$: Observable<boolean>;
  widgetsSorted$: Observable<Array<Widget>>;
  widgetsSortableList: Widget[] = [];
  widgetsLoading$: Observable<boolean>;
  isDownlaoding$: Observable<boolean>;
  countSelectedWidgets$: Observable<number>;
  currentObjectEditMode$: Observable<boolean>;
  selectSelectedPlaceholderId$: Observable<string>;
  filteredSidebarButtons$: Observable<SidebarButtonConfig[]>;
  loadingAlgorithmPostIds$: Observable<string[]>;
  appliedAlgorithmPostIds$: Observable<string[]>;
  currentPage$: Observable<number>;
  itemsPerPage$: Observable<number>;
  totalItemsCount$: Observable<number>;
  searchWidgets$ = new Subject<string>();

  private resizeObserver: ResizeObserver;
  private refreshWidgetList$ = new Subject<void>();
  private sidebarButtons$ = new BehaviorSubject<SidebarButtonConfig[]>([]);
  private gridOptionsCache = new Map<string, GridStackNode>();

  user: User;
  feed: Feed;
  feedId: string;
  isStoryFeed = false;
  selectedPostId: string;
  widgetEditMode: boolean;
  debouncedSearchPosts: any;
  debouncedSearchWidgets: any;
  searchBuilderData: SearchBuilderData;
  sidebarButtons: SidebarButtonConfig[];
  feedViewConfig: PropertyViewConfig[];
  feedCategoryIds: number[];
  timeShown: string;
  feedSearchQuery: string;
  widgetSearchString: string;
  intervalId: any;

  gridInitialized = false;
  // Set at the start of ngOnDestroy so the feed$ subscription can tell a
  // genuine "feed not found" apart from the expected null emitted when
  // `feedUnloaded` resets the store during teardown (navigate-away).
  private isDestroyed = false;

  WidgetType = WidgetType;
  NomDateTimeFormats = NomDateTimeFormats;
  paneClass = 'pane-default';
  viewMode: FeedPostViewMode = 'default';
  validViewModes: FeedPostViewMode[] = [
    'default',
    'threads',
    'compact',
    'gallery',
  ];
  currentLanguage = this.translateService.currentLang;
  private pendingScrollWidgetId: string | null = null;

  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  private advancedSearchService = inject(AdvancedSearchService);

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.altKey && event.key.toLowerCase() === 'r') {
      event.preventDefault();
      this.feedRefresh();
    }
  }

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private sidebarPanelService: PanelComponentMappingService,
    private activatedRoute: ActivatedRoute,
    private formService: FormsService,
    public utilsService: UtilsService,
    public objectActionService: ObjectActionsService,
    public widgetService: WidgetService,
    public sidebarService: SidebarService,
    private translateService: TranslateService,
    private logger: LoggerService,
    public userFacade: UserFacadeService,
    private gridsterConfigService: GridsterConfigService,
    private objectEditorService: ObjectEditorService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
  ) {
    // Initialize grid configuration from service
    this.gridstackOptions = this.gridsterConfigService.getFeedConfig();

    this.feed$ = this.store.select(selectFeed);
    this.objectViewProperties$ = this.store.select(
      selectObjectWidgetViewProperties,
    );
    this.selectedPostId$ = this.store.select(selectSelectedPostId);
    this.countSelectedWidgets$ = this.store.select(selectCountSelectedWidgets);
    this.isDownlaoding$ = this.store.select(selectObjectIsExporting);
    this.currentObjectEditMode$ = this.store.select(
      selectCurrentObjectEditMode,
    );

    this.selectedPostId$
      .pipe(
        tap((id) => {
          this.selectedPostId = id;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUser)
      .pipe(
        tap((user) => {
          this.user = user;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectFeedViewConfig)
      .pipe(
        tap((config) => {
          this.logger.debug('Setting feedViewConfig', config);
          this.feedViewConfig = config;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.selectSelectedPlaceholderId$ = this.store.select(
      selectSelectedPlaceholderId,
    );

    this.posts$ = this.store.select(selectFeedPosts).pipe(
      map((posts) => {
        return [...posts.values()];
      }),
    );

    this.postsLoading$ = this.store.select(selectFeedPostsLoading);
    this.widgetsLoading$ = this.store.select(selectIsLoadingWidgets);

    this.currentPage$ = this.store.select(selectFeedCurrentPage);
    this.itemsPerPage$ = this.store.select(selectFeedItemsPerPage);
    this.totalItemsCount$ = this.store.select(selectFeedTotalItemsCount);

    // * Shared observable that replays the last emitted value to new subscribers
    this.widgets$ = this.store.select(selectWidgetsByFilterString).pipe(
      map((widgets) => [...widgets.values()]),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // * Pre-built, reference-stable gridstack options per widget: the
    // * gridstack-item [options] setter calls grid.update() whenever the input
    // * reference changes, so an inline template literal would re-layout every
    // * widget on every change-detection cycle.
    this.widgetsWithGridOptions$ = this.widgets$.pipe(
      map((widgets) => {
        const cache = new Map<string, GridStackNode>();
        const items = widgets.map((widget) => {
          const gridOptions = this.toGridOptions(widget);
          cache.set(widget.id, gridOptions);
          return { widget, gridOptions };
        });
        this.gridOptionsCache = cache;
        return items;
      }),
    );

    this.searchWidgets$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap((query) => {
          this.widgetSearchString = query;
          this.store.dispatch(widgetActions.searchWidgets({ query }));
          // * the sortable list only refreshes on-demand — sync it with the
          // * filtered store state (sorting itself is disabled while searching)
          if (this.widgetEditMode) {
            this.refreshWidgetList$.next();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Use the shared observable and sort it for the sorted widgets observable
    this.widgetsSorted$ = this.widgets$.pipe(
      map((widgets) => [...widgets].sort((a, b) => a.y - b.y)),
    );

    // * seed fixed array for SortableListDirective ONCE! (avoid stream overwrites during drag)
    // * refreshes on-demand via refreshWidgetList$ trigger
    this.refreshWidgetList$
      .pipe(
        switchMap(() => this.widgetsSorted$.pipe(take(1))),
        tap((widgets) => {
          this.widgetsSortableList = widgets.filter(
            (w) => w.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
          );

          this.cdr.detectChanges();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.feed$
      .pipe(
        tap((feed) => {
          if (!feed) {
            // Skip during teardown: `feedUnloaded` (dispatched in ngOnDestroy)
            // resets the store to a null feed, which is expected — not an error.
            if (!this.isDestroyed) {
              this.logger.error('Feed not found');
            }
            return;
          }
          this.feed = feed;
          this.isStoryFeed = feed.object_type_id === NomObjectTypes.STORY_FEED;
          this.estimatePaneClass(feed);
          // Assuming that this.feed.view_posts_mode might be any string, we validate it.
          this.viewMode = this.validViewModes.includes(
            this.feed.view_posts_mode,
          )
            ? (this.feed.view_posts_mode as FeedPostViewMode)
            : 'default';

          this.setupAutoRefresh(feed);
          this.loadFeedCategoryTreeIfNeeded(feed);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.route.data
      .pipe(
        tap((data) => {
          if (data['feed']) {
            this.store.dispatch(objectChanged({ object: data['feed'] }));
            this.setupSidebar(data['feed']);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.route.params
      .pipe(
        tap((params) => {
          const feedId = params['feedId'];
          this.feedId = feedId;

          this.store.dispatch(feedActions.loadFeedPosts({ feedId }));
          this.store.dispatch(feedActions.loadFeedAlgorithms({ feedId }));
          this.store.dispatch(
            widgetActions.loadWidgets({
              objectId: feedId,
              objectClassId: NomObjectClass.FEED,
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.filteredSidebarButtons$ = combineLatest([
      this.sidebarButtons$,
      this.store.select(selectUser),
      this.store.select(selectFeedSelectedFilterItems),
      this.store.select(selectFeedAlertsCount),
      this.store.select(selectExportSchedulesCount),
      this.store.select(selectRemovedItemsByFeed),
      this.store.select(selectAttachedFeedAlgorithms),
    ]).pipe(
      map(
        ([
          buttons,
          user,
          activeFilterItems,
          alertsCount,
          exportSchedulesCount,
          removedItems,
          attachedAlgorithms,
        ]) => {
          return buttons
            ?.map((button) => {
              const updatedButton = { ...button };

              // * add counter for filters
              if (button.key === 'filters') {
                updatedButton.counter = activeFilterItems.length;
              }

              if (button.key === 'alerts') {
                updatedButton.counter = alertsCount;
              }

              if (button.key === 'schedules') {
                updatedButton.counter = exportSchedulesCount;
              }

              if (button.key === 'removed-items') {
                updatedButton.counter = removedItems.length;
              }

              if (button.key === 'algorithms') {
                updatedButton.counter = attachedAlgorithms.length;
              }

              return updatedButton;
            })
            .filter((button) => button.shouldDisplay !== false);
        },
      ),
    );

    this.store
      .select(selectCurrentObject)
      .pipe(
        tap((object) => {
          const shouldHideSearch =
            object?.object_type_id === NomObjectTypes.CLIPPING_FEED;
          // Update sidebar buttons' display flag based on header value.
          this.sidebarButtons?.forEach((button) => {
            button.shouldDisplay =
              button.key === 'search' ? !shouldHideSearch : true;
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectFeedTimeShown)
      .pipe(
        tap((timeShown) => {
          this.timeShown = timeShown;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.loadingAlgorithmPostIds$ = this.store.select(
      selectLoadingAlgorithmPostIds,
    );
    this.appliedAlgorithmPostIds$ = this.store.select(
      selectAppliedAlgorithmPostIds,
    );

    this.advancedSearchService.searchQuery$
      .pipe(
        tap((query) => {
          this.feedSearchQuery = query;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(
          widgetActions.createWidgetSuccess,
          widgetActions.updateWidgetSuccess,
          widgetActions.deleteWidgetSuccess,
          widgetActions.deleteWidgetsSuccess,
          widgetActions.massUpdateWidgetsSuccess,
        ),
        tap(() => {
          // Only refresh if we're currently in edit mode
          if (this.widgetEditMode) {
            this.refreshWidgetList$.next();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(loadWidgetsSuccess),
        tap(() => {
          if (this.widgetEditMode) {
            this.refreshWidgetList$.next();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectWidgetEditMode)
      .pipe(
        tap((editMode: boolean) => {
          this.widgetEditMode = editMode;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    // * trigger initial widget list load
    this.refreshWidgetList$.next();

    this.setupRouteParamsHandling();
    this.setupWidgetCreationListener();
    this.setupFeedChangeListener();

    window.addEventListener('message', this.handleIFrameMessage);

    // this.store
    //   .select(selectFeedViewConfig)
    //   .pipe(
    //     tap((config) => this.logger.debug('selectFeedViewConfig', config)),
    //     takeUntilDestroyed(this.destroyRef),
    //   )
    //   .subscribe();
  }

  // * Loads the category tree whenever the current feed's categories change —
  // * ngOnInit alone misses feed-to-feed navigation (the component is reused).
  private loadFeedCategoryTreeIfNeeded(feed: Feed): void {
    const categoryIds =
      feed.company_categories?.map(
        (category) => category.company_category_id,
      ) ?? [];

    const previousIds = this.feedCategoryIds ?? [];
    const unchanged =
      categoryIds.length === previousIds.length &&
      categoryIds.every((id, i) => id === previousIds[i]);
    if (unchanged) {
      return;
    }

    this.feedCategoryIds = categoryIds;
    if (categoryIds.length > 0) {
      this.store.dispatch(
        feedActions.loadFeedCategoryTree({
          feedCategoryIds: categoryIds,
        }),
      );
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => {
        this.currentLanguage = language.lang;
      });

    // Calculate initial cell height and set up resize listener
    setTimeout(() => {
      this.calculateCellHeight();
    });

    // Recalculate on window resize
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        tap(() => this.calculateCellHeight()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const open = params['open'];
        if (!open || !this.feed) {
          return;
        }
        if (open == 'alerts') {
          this.store.dispatch(
            feedActions.loadFeedAlerts({ feedId: this.feed.id }),
          );
          const alertsButton = this.sidebarButtons?.find(
            (button) => button.key === 'alerts',
          );
          if (alertsButton) {
            this.sidebarService.selectComponent(alertsButton, true);
          }
        } else if (open == 'export-schedules') {
          this.store.dispatch(
            loadExportSchedules({
              objectId: this.feed.id,
              objectClassId: this.feed.object_class_id,
            }),
          );
          const schedulesButton = this.sidebarButtons?.find(
            (button) => button.key === 'schedules',
          );
          if (schedulesButton) {
            this.sidebarService.selectComponent(schedulesButton, true);
          }
        }
      });
  }

  ngAfterViewChecked(): void {
    // ! check if we need to scroll to a newly created widget
    if (this.pendingScrollWidgetId) {
      const widgetElement = document.getElementById(
        `widget-${this.pendingScrollWidgetId}`,
      );
      if (widgetElement) {
        this.scrollToWidget(this.pendingScrollWidgetId);
        this.pendingScrollWidgetId = null;
      }
    }
  }

  ngOnDestroy(): void {
    // Mark teardown BEFORE dispatching feedUnloaded — that dispatch synchronously
    // resets the store, pushing a null feed through the still-live feed$ subscription.
    this.isDestroyed = true;
    // Dispatch an action when the component is destroyed
    this.logger.debug('Feed component destroyed');
    this.store.dispatch(feedActions.feedUnloaded());
    if (this.feed) {
      this.store.dispatch(
        clearSelectedPrincipals({ companyId: this.feed.company_id }),
      );
    }

    this.sidebarService.closeSidebar();
    // Remove the event listener to prevent memory leaks
    window.removeEventListener('message', this.handleIFrameMessage);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    clearInterval(this.intervalId);
  }

  private setupGridstackEventListeners(): void {
    if (!this.gridstack?.grid) {
      return;
    }

    // ! this is the better way to track the Widget that is being resized
    // ! do not try with changeCB resize start/end events - it will not work as expected
    this.gridstack.grid.on('resizestart', (event, el) => {
      const widgetId = el.gridstackNode?.id;
      if (widgetId) {
        this.resizingWidgetId$.next(widgetId.toString());
      }
    });

    this.gridstack.grid.on('resizestop', (event, el) => {
      this.resizingWidgetId$.next(null);
    });

    this.gridstack.grid.on('dragstart', (event, el) => {
      this.isDragging$.next(true);
    });

    this.gridstack.grid.on('dragstop', (event, el) => {
      this.isDragging$.next(false);
    });
  }

  setupSidebar(feed: Feed) {
    const buttons: SidebarButtonConfig[] = [
      {
        label: this.translateService.instant('sidebar-tools.label.settings'),
        key: 'settings',
        header: this.translateService.instant('sidebar-tools.label.settings'),
        icon: ['fas', 'gear'],
        behaviorType: SidebarButtonBehavior.ACTION,
        actionHandler: () => {
          this.objectEditorService.loadFeedAndOpenPopup(feed.id);
        },
        permission: AppFeatures.FEED_SETTINGS_ROOT,
        objectPermission: ['can_edit'],
      },
      {
        label: this.translateService.instant(
          'sidebar-tools.label.search-builder',
        ),
        componentName: 'SearchBuilderWrapperComponent',
        header: this.translateService.instant(
          'sidebar-tools.label.search-builder',
        ),
        key: 'search-builder',
        icon: ['fas', 'search'],
        permission: AppFeatures.FEED_SETTINGS_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_edit'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.widgets'),
        componentName: 'WidgetSidebarComponent',
        key: 'widgets',
        header: this.translateService.instant('sidebar-tools.label.widgets'),
        icon: ['fas', 'chart-bar'],
        permission: AppFeatures.WIDGETS_LIBRARY_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },

        objectPermission: ['can_edit'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.filters'),
        componentName: 'FiltersComponent',
        key: 'filters',
        header: this.translateService.instant('sidebar-tools.label.filters'),
        icon: ['fas', 'filter'],
        permission: AppFeatures.FACET_FILTERS_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.alerts'),
        componentName: 'AlertsComponent',
        header: this.translateService.instant('sidebar-tools.label.alerts'),
        key: 'alerts',
        icon: ['fas', 'bell'],
        permission: AppFeatures.ALERTS_MANAGER_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.export'),
        componentName: 'ExportComponent',
        header: this.translateService.instant('sidebar-tools.label.export'),
        key: 'export',
        icon: ['fas', 'share-nodes'],
        permission: AppFeatures.EXPORT_AND_SHARE_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
          shareMode: ShareMode.RESULTSET,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant('sidebar-tools.label.schedules'),
        componentName: 'SchedulesComponent',
        header: this.translateService.instant('sidebar-tools.label.schedules'),
        key: 'schedules',
        icon: ['fas', 'clock-rotate-left'],
        permission: AppFeatures.EXPORT_AND_SHARE_SCHEDULES,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_view'],
      },
      {
        label: this.translateService.instant(
          'sidebar-tools.label.scheduled-algorithms',
        ),
        componentName: 'AlgorithmsComponent',
        header: this.translateService.instant(
          'sidebar-tools.label.scheduled-algorithms',
        ),
        key: 'algorithms',
        icon: ['fas', 'microchip'],
        permission: AppFeatures.FEED_SETTINGS_ROOT,
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
        objectPermission: ['can_edit'],
      },
    ];

    this.sidebarButtons = buttons.filter(
      (b) =>
        this.userFacade.hasAllObjectPermissionsSync(
          feed,
          b.objectPermission ?? [],
        ) &&
        (!b.permission ||
          this.userFacade.hasPermissionSync(b.permission) === true),
    );
    this.sidebarButtons$.next(this.sidebarButtons);
  }

  // ! Add grid change handler method
  onGridChange(event: any) {
    if (event && event.nodes && event.nodes.length > 0) {
      // * map gridstack.js format to our action format
      const repositionedWidgets = event.nodes.map((node) => ({
        id: node.id,
        x: node.x,
        y: node.y,
        w: node.w,
        h: node.h,
      }));

      this.store.dispatch(
        widgetActions.widgetsRepositioned({ widgets: repositionedWidgets }),
      );
    }
  }

  // * Estimates the pane class based on the number of views valid in the feed.
  estimatePaneClass(feed: Feed): void {
    const validViews = new Set(['list', 'details', 'analytics']);
    const paneClasses = ['pane-single', 'pane-double', 'pane-triple'];

    const viewSize =
      feed?.views?.reduce(
        (count, view) => (validViews.has(view) ? count + 1 : count),
        0,
      ) ?? 0;

    this.paneClass =
      viewSize > 0
        ? paneClasses[Math.min(viewSize - 1, paneClasses.length - 1)]
        : 'pane-default';
  }

  onDataChange(data) {
    this.logger.debug('Data changed', data);
  }

  setFeedEditMode(editMode: boolean) {
    this.store.dispatch(feedActions.setFeedEditMode({ editMode: editMode }));
  }

  loadComponent(sidebarButton: SidebarButtonConfig): void {
    this.sidebarPanelService.getChildInstance().loadComponent(sidebarButton);
  }

  onFormGroupChange(formGroup: FormGroup) {
    this.logger.debug('search builder formGroup', formGroup);
    const socialSourceIds =
      this.formService.extractFiltersFormDataPropertyValues(
        formGroup.value,
        'post_social_source_id',
      );

    this.logger.debug('socialSourceIds', socialSourceIds);
    this.searchBuilderData = formGroup.value;
    this.searchBuilderData.social_source_id = socialSourceIds;
  }

  // * Edits the feed with the provided data.
  editFeed() {
    const feedData = {
      feed_id: this.feedId,
      workspace_id: this.feed.workspace_id,
      query_text: this.searchBuilderData.query_text,
      title: this.feed.title,
      query_created_from: this.searchBuilderData.query_created_from,
      params: {
        social_sources: this.searchBuilderData.social_source_id,
        combined_feeds: this.searchBuilderData.combined_feeds,
        social_facebook_comments_status:
          this.searchBuilderData.social_facebook_comments_status,
      },
    };

    if (this.searchBuilderData.filters) {
      feedData['filters'] = this.searchBuilderData.filters;
    }

    this.store.dispatch(
      feedActions.editFeed({ feedId: this.feedId, feedData: feedData }),
    );
  }

  setupRouteParamsHandling() {
    // Accessing query params
    this.activatedRoute.queryParams
      .pipe(
        tap((params) => {
          const startTimestamp = params['start_time'];
          const endTimestamp = params['end_time'];

          if (startTimestamp && endTimestamp) {
            this.store.dispatch(
              periodChanged({
                period: {
                  startTimestamp: startTimestamp,
                  endTimestamp: endTimestamp,
                },
              }),
            );
          }

          const page = params['page'];

          if (page) {
            // Assuming 'page' is a number
            this.store.dispatch(feedActions.pageChanged({ pageNumber: page }));
          }

          if (params['filters']) {
            // Assuming 'filters' is a JSON-encoded string of the array of filters
            try {
              const filters = JSON.parse(params['filters']);
              // TODO: dispatch an action to apply these filters once the
              // corresponding feed action exists — currently parsed but not used.
              this.logger.debug('Parsed filters from query params', filters);
            } catch (error) {
              this.logger.error('Error parsing filters', error);
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onTogglePostDetails(
    $event: { isDetailsView: boolean; postId: string },
    index: number,
  ) {
    this.openPostDetails($event.postId, index);

    this.widgetService.widgetResized.emit();
    this.postList?.scrollToPost(index);
  }

  closePostDetails(): void {
    this.store.dispatch(openPostDetails({ postId: null }));
  }

  handleSearchStringWidgetChange(query: string) {
    // Use the debounced function instead of the original search function
    if (typeof query === 'string') {
      this.searchWidgets$.next(query);
    }
  }

  selectAllWidgets(selected: boolean) {
    if (selected) {
      this.widgets$.pipe(take(1)).subscribe((widgets) => {
        widgets = widgets.filter(
          (w) => w.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
        );
        this.store.dispatch(selectObjects({ objects: widgets }));
      });
    } else {
      this.store.dispatch(clearSelectedObjects());
    }

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);
  }

  widgetConfigure() {
    this.store.dispatch(toggleWidgetEditMode());
    if (this.widgetEditMode) {
      this.refreshWidgetList$.next();
    }
    setTimeout(() => {
      this.calculateCellHeight();
    });
  }

  widgetRefresh() {
    if (this.widgetEditMode) return;

    this.widgets$
      .pipe(
        take(1),
        tap((widgets) =>
          widgets.forEach((widget) =>
            this.store.dispatch(
              widgetActions.loadWidget({ widgetId: widget.id }),
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  feedConfigure() {
    this.store.dispatch(feedActions.toggleFeedEditMode());
  }

  feedRefresh() {
    this.store.dispatch(feedActions.loadFeedPosts({ feedId: this.feed.id }));
  }

  openFeedSettings() {
    this.objectEditorService.loadFeedAndOpenPopup(this.feed.id);
  }

  private setupAutoRefresh(feed: Feed): void {
    clearInterval(this.intervalId);
    this.intervalId = null;

    if (!feed?.is_auto_refresh || !feed.auto_refresh_seconds) {
      return;
    }

    const intervalTime = feed.auto_refresh_seconds * 1000;
    this.intervalId = setInterval(() => {
      this.store.dispatch(feedActions.loadFeedPosts({ feedId: feed.id }));
    }, intervalTime);
  }

  onPageChanged(page: number) {
    this.store.dispatch(feedActions.pageChanged({ pageNumber: page }));
  }

  onItemsPerPageChanged(itemsPerPage: number) {
    this.store.dispatch(feedActions.itemsPerPageChanged({ itemsPerPage }));
  }

  widgetTrackByFn(index: number, item: { widget: Widget }): string {
    return item.widget.id;
  }

  // * Reuses the cached options object when the widget's geometry is unchanged,
  // * so gridstack-item only sees a new input when something actually moved.
  private toGridOptions(widget: Widget): GridStackNode {
    const noResize =
      widget.widget_type_id === WidgetType.WIDGET_TYPE_PLACEHOLDER;
    const cached = this.gridOptionsCache.get(widget.id);
    if (
      cached &&
      cached.x === widget.x &&
      cached.y === widget.y &&
      cached.w === widget.cols &&
      cached.h === widget.rows &&
      cached.noResize === noResize
    ) {
      return cached;
    }
    return {
      x: widget.x,
      y: widget.y,
      w: widget.cols,
      h: widget.rows,
      id: widget.id,
      noResize,
    };
  }

  postTrackByFn(index: number, post: Post): string {
    return post.id;
  }

  onWidgetListSorted(result: SortableResult): void {
    // * update local array
    this.widgetsSortableList = cloneDeep(result.items);

    const updatedWidgets = this.updateWidgetPositions(result.items);

    this.store.dispatch(
      widgetActions.widgetsResizedBatch({
        widgets: updatedWidgets.map((widget) => ({
          widgetId: widget.id,
          x: widget.x,
          y: widget.y,
          cols: widget.cols,
          rows: widget.rows,
        })),
      }),
    );
  }

  updateWidgetPositions(widgets: Widget[]): Widget[] {
    let currentY = 0;

    return widgets.map((widget) => {
      // Assign the current Y position to the widget
      const updatedWidget = { ...widget, y: currentY };
      // Update the current Y position for the next widget
      currentY += widget.rows;

      return updatedWidget;
    });
  }

  private handleIFrameMessage = (event: MessageEvent) => {
    const type = event.data?.type;

    if (type === 'widgetClick') {
      const record = event.data.payload;
      this.logger.debug('Widget click event received', record);
      this.store.dispatch(widgetActions.htmlWidgetClicked({ payload: record }));
      return;
    }

    if (type === 'widgetSortChange') {
      const { widget_id: widgetId, filter } = event.data.payload || {};
      if (!widgetId || !filter) {
        return;
      }

      this.store
        .select(selectWidgetById(widgetId))
        .pipe(take(1))
        .subscribe((widget) => {
          if (!widget) {
            return;
          }

          const updated = {
            ...widget,
            settings: {
              ...widget.settings,
              order_by_field: {
                ...(widget.settings?.order_by_field || {}),
                filter,
              },
            },
          };

          this.store.dispatch(
            widgetActions.updateWidget({ widgetId, data: updated }),
          );
        });

      return;
    }

    if (type === 'share-video-segment') {
      const shareParams = event.data.payload;
      const playerUrl = shareParams.playerURL as string;

      this.store
        .select(selectPostByVideoUrlTargetParam(playerUrl))
        .pipe(take(1))
        .subscribe((post) => {
          if (!post) {
            this.logger.warn(
              'No post found for video url',
              playerUrl,
              shareParams,
            );

            this.toastService.showError(
              this.translateService.instant('video-share.error.post-not-found'),
            );
            return;
          }

          this.objectEditorService.sharePost(post, shareParams);
        });

      return;
    }
  };

  // * Opens the details of a post.
  private openPostDetails(postId: string, index: number): void {
    this.store.dispatch(openPostDetails({ postId: postId }));

    this.store.dispatch(
      feedActions.toggleFeedView({
        feedId: this.feedId,
        view: 'details',
        selected: true,
      }),
    );
  }

  public remToPx(remStr) {
    const rem = parseFloat(remStr); // e.g., "1rem" -> 1
    const appRemPx = 14; // px
    return Math.round(rem * appRemPx); // use Math.floor/ceil if you prefer
  }

  // * Make Gridstack cell half the width of the cell
  private calculateCellHeight() {
    if (this.gridstack?.el) {
      const containerWidth = this.gridstack.el.getBoundingClientRect().width;
      const margin = this.remToPx(this.gridstackOptions.margin);
      const column = Number(this.gridstackOptions.column);

      const margins = margin * (column - 1);
      const columnWidth = (containerWidth - margins) / column;
      const cellHeight = columnWidth / 2;

      // * Update grid with new cellHeight
      this.gridstack.el.gridstack.cellHeight(cellHeight);
    }
  }

  selectSectionBreakWidget(clickedWidget: Widget): void {
    this.widgetsSorted$
      .pipe(
        take(1),
        tap((widgets) => {
          const startIndex = widgets.findIndex(
            (w) => w.id === clickedWidget.id,
          );
          if (startIndex === -1) return;

          for (let i = startIndex + 1; i < widgets.length; i++) {
            const widget = widgets[i];

            if (
              widget.widget_type_id === WidgetType.WIDGET_TYPE_SECTION_BREAK
            ) {
              // Stop if we hit another section break
              break;
            }

            // Only select widgets that are NOT section breaks
            this.objectActionService.toggleObjectSelection(widget);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onRequestToggleThread({ postId }: { postId: string }) {
    const cmp = this.postList?.postCardCmps?.find((c) => c.post.id === postId);
    if (!cmp?.card) {
      this.logger.error(
        'Post card component or card element not found' + postId,
      );
      return;
    }

    const cardEl = cmp.card.nativeElement;
    const beforeTop = cardEl.getBoundingClientRect().top;

    this.store.dispatch(
      togglePostParentThread({ postId, feedId: this.feed.id }),
    );

    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      const afterTop = cardEl.getBoundingClientRect().top;
      const delta = afterTop - beforeTop;

      const scroller = this.postList?.cardContainer?.nativeElement ?? window;
      if (scroller instanceof HTMLElement) {
        scroller.scrollTop += delta;
      } else {
        window.scrollTo({ top: window.scrollY + delta });
      }
    });
  }

  onDragOver(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();
      event.dataTransfer!.dropEffect = 'copy';
    });
  }

  onDrop(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();

      // ! parse JSON outside zone
      const dragData = this.getDragDataOutsideZone(event);

      if (dragData?.type === 'widget-visualization-type') {
        // ! re-enter zone only for state updates
        this.ngZone.run(() => {
          this.createWidgetFromDrop(dragData);
        });
      }
    });
  }

  private createWidgetFromDrop(dragData: any): void {
    this.store.dispatch(
      createWidget({
        chartId: dragData.chartId,
        chartTypeId: dragData.chartTypeId,
        objectId: this.feed.id,
        objectClassId: NomObjectClass.FEED,
        placeholderId: null,
        dataSourceObjectId: dragData.dataSourceObjectId,
        dataSourceObjectClassId: dragData.dataSourceObjectClassId,
      }),
    );
  }
  private setupFeedChangeListener(): void {
    this.actions$
      .pipe(
        ofType(feedActions.loadFeedSuccess),
        takeUntilDestroyed(this.destroyRef),
        tap((action) => {
          this.feed = action.feed;
          this.isStoryFeed =
            action.feed.object_type_id === NomObjectTypes.STORY_FEED;
          this.setupSidebar(action.feed);
          this.estimatePaneClass(action.feed);
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }
  private setupWidgetCreationListener(): void {
    this.actions$
      .pipe(
        ofType(widgetActions.createWidgetSuccess),
        takeUntilDestroyed(this.destroyRef),
        tap((action) => {
          this.pendingScrollWidgetId = action.widget.id;
        }),
      )
      .subscribe();
  }

  private scrollToWidget(widgetId: string): void {
    const widgetElement = document.getElementById(`widget-${widgetId}`);
    const scrollContainer = document.querySelector('.card-container.widgets');

    if (widgetElement && scrollContainer) {
      // TODO: this is working only with block: 'nearest'
      // TODO: other options like 'center' are pushing the parents up
      // TODO: there are other ways to scroll using getBoundingClientRect and scrollTo
      widgetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  private getDragDataOutsideZone(event: DragEvent): any {
    try {
      const data = event.dataTransfer?.getData('application/json');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  }
}
